import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { Icon } from '@iconify/react/dist/iconify.js';

const UserForm = () => {
    // === Constants ===
    const ACCESS_OPTIONS = ["all", "own", "franchise", "partnership"];
    const ROLE_OPTIONS = ["admin", "director", "manager"];
    const SALUTATION_OPTIONS = [
        { value: 'Mr.', label: 'Mr.' },
        { value: 'Ms.', label: 'Ms.' },
        { value: 'Mrs.', label: 'Mrs.' },
    ];

    const ALL_CENTERS = [
        { id: 1, name: "Center A", type: "own" },
        { id: 2, name: "Center B", type: "own" },
        { id: 3, name: "Center C", type: "franchise" },
        { id: 4, name: "Center D", type: "partnership" },
        { id: 5, name: "Center E", type: "franchise" },
    ];

    const ROLE_MODULES = {
        admin: [
            { id: 1, name: "User Management" },
            { id: 2, name: "Reporting" },
            { id: 3, name: "System Config" },
            { id: 4, name: "All Modules" },
        ],
        director: [
            { id: 2, name: "Reporting" },
            { id: 5, name: "Finance" },
            { id: 1, name: "User Management" },
        ],
        manager: [
            { id: 6, name: "Day-to-Day Operations" },
            { id: 7, name: "Staff Management" },
        ],
    };



    // === State ===
    const [salutation, setSalutation] = useState(SALUTATION_OPTIONS[0].value);
    const [formData, setFormData] = useState({
        salutation: salutation,
        firstName: "",
        lastName: "",
        email: "",
        address: "",
        designation: "",
        phoneNumber: "",
        username: "",
        password: "",
        role: "",
        accessCenter: "",
        selectedCenters: [],
        selectedModules: [],
        profilePhoto: null,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState('');

    // === Derived options ===
    const centerOptions = ALL_CENTERS.map((c) => ({
        value: c.id,
        label: `${c.name} (${c.type})`,
        type: c.type,
    }));

    const moduleOptions = useMemo(() => {
        const allModules = Object.values(ROLE_MODULES).flat();
        const uniqueModules = Array.from(new Map(allModules.map((m) => [m.id, m])).values());
        return uniqueModules.map((m) => ({ value: m.id, label: m.name }));
    }, [ROLE_MODULES]);

    // === Handlers ===
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };


    // === Handle Access Center Changes ===
    const handleAccessTypeChange = (e) => {
        const newType = e.target.value;
        setFormData((prev) => ({ ...prev, accessCenter: newType }));

        if (newType === "all") {
            setFormData((prev) => ({
                ...prev,
                selectedCenters: centerOptions.map((opt) => opt.value),
            }));
        } else {
            const filteredCenters = centerOptions
                .filter((opt) => opt.type === newType)
                .map((opt) => opt.value);
            setFormData((prev) => ({
                ...prev,
                selectedCenters: filteredCenters,
            }));
        }
    };

    const handleCentersChange = (selected) => {
        setFormData((prev) => ({
            ...prev,
            selectedCenters: selected ? selected.map((s) => s.value) : [],
        }));
    };

    // === Handle Role & Module Changes ===
    const handleRoleChange = (e) => {
        const newRole = e.target.value;
        const defaultModules = ROLE_MODULES[newRole]?.map((m) => m.id) || [];

        setFormData((prev) => ({
            ...prev,
            role: newRole,
            selectedModules: defaultModules,
        }));
    };

    const handleModulesChange = (selected) => {
        setFormData((prev) => ({
            ...prev,
            selectedModules: selected ? selected.map((s) => s.value) : [],
        }));
    };
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // === Submit ===
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const payload = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (Array.isArray(value)) payload.append(key, JSON.stringify(value));
            else payload.append(key, value);
        });

        console.log("🚀 Submitting payload:", Object.fromEntries(payload.entries()));
        await new Promise((r) => setTimeout(r, 1000));
        alert("User saved successfully!");

        setIsSubmitting(false);
    };





    return (
        <>
            <div className="pb-5">
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="row">

                        <div className="col-6">
                            <div className="card shadow-sm p-4 border-0 rounded-4">

                                {/* === Personal Info === */}
                                <SectionHeader title="Personal Details" />
                                <div className="row ">
                                    <div className="col-4">
                                        <h6 className="text-md text-primary-light mb-16">Profile Image</h6>
                                        {/* Upload Image Start */}
                                        <div className="mb-24 mt-16">
                                            <div className="avatar-upload">
                                                <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                                                    <input
                                                        type="file"
                                                        id="imageUpload"
                                                        accept=".png, .jpg, .jpeg"
                                                        hidden
                                                        onChange={handleImageChange}
                                                    />
                                                    <label
                                                        htmlFor="imageUpload"
                                                        className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle">
                                                        <Icon icon="solar:camera-outline" className="icon"></Icon>
                                                    </label>
                                                </div>
                                                <div className="avatar-preview" style={{width:"190px", height:"190px"}}>
                                                    <div
                                                        id="imagePreview"
                                                        style={{
                                                            backgroundImage: imagePreviewUrl ? `url(${imagePreviewUrl})` : '',

                                                        }}
                                                    >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Upload Image End */}

                                    </div>
                                    <div className="col-8">
                                        <div className="row">
                                            <div className="col-6">
                                                <label className="form-label fw-semibold ">Salutation<span className="text-danger" >*</span></label>

                                                <Select
                                                    options={SALUTATION_OPTIONS}
                                                    placeholder="Select Salutation"
                                                    value={SALUTATION_OPTIONS.find(opt => opt.value === salutation)}
                                                    onChange={(selected) => setSalutation(selected.value)}


                                                />

                                            </div>

                                            <InputField name="firstName" label="First Name" value={formData.firstName} onChange={handleInputChange} required col="6" />
                                            <InputField name="lastName" label="Last Name" value={formData.lastName} onChange={handleInputChange} required col="6" />
                                            <InputField name="phoneNumber" label="Phone Number" type="tel" value={formData.phoneNumber} onChange={handleInputChange} required col="6" />
                                            <InputField name="email" label="Email" type="email" value={formData.email} onChange={handleInputChange} required col="12" />
                                            <InputField name="designation" label="Designation" value={formData.designation} onChange={handleInputChange} col="6" />
                                            <InputField name="address" label="Address" value={formData.address} onChange={handleInputChange} col="6" />
                                            <InputField name="username" label="User Name" type="text" value={formData.username} onChange={handleInputChange} col="6" required />
                                            <InputField name="password" label="Password" type="password" value={formData.password} onChange={handleInputChange} col="6" required />

                                        </div>

                                    </div>




                                </div>



                                <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
                                    {isSubmitting ? "Saving..." : "Submit User Details"}
                                </button>

                            </div>

                        </div>
                        <div className="col-6">
                            <div className="card shadow-sm p-4 border-0 rounded-4">

                                {/* === Access Center === */}
                                <SectionHeader title="Center Access" />
                                <div className="d-flex flex-wrap gap-4 mb-3">
                                    {ACCESS_OPTIONS.map((option) => (
                                        <div className="form-check gap-1 d-flex align-items-center" key={option}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="accessCenter"
                                                id={`access-${option}`}
                                                value={option}
                                                checked={formData.accessCenter === option}
                                                onChange={handleAccessTypeChange}
                                            />
                                            <label className="form-check-label" htmlFor={`access-${option}`}>
                                                {option.charAt(0).toUpperCase() + option.slice(1)}
                                            </label>
                                        </div>
                                    ))}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Select Centers<span className="text-danger">*</span></label>
                                    <Select
                                        isMulti
                                        options={centerOptions}
                                        value={centerOptions.filter((opt) =>
                                            formData.selectedCenters.includes(opt.value)
                                        )}
                                        onChange={handleCentersChange}
                                        placeholder="Select centers..."
                                    />
                                    
                                </div>

                                {/* === Role & Modules === */}
                                <SectionHeader title="Roles & Modules" />
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Select Role<span className="text-danger">*</span></label>
                                     <div className="d-flex flex-wrap gap-4">
                                        {ROLE_OPTIONS.map(role => (
                                            <div className="form-check gap-1 d-flex align-items-center" key={role}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="role"
                                                id={`role-${role}`}
                                                value={role}
                                                checked={formData.role === role}
                                                onChange={handleRoleChange}
                                            />
                                            <label className="form-check-label" htmlFor={`role-${role}`}>
                                                {role.charAt(0).toUpperCase() + role.slice(1)}
                                            </label>
                                        </div>

                                        ))

                                        }
                                     </div>
                                    
                                </div>

                                

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Select Modules <span className="text-danger">*</span> </label>
                                    <Select
                                        isMulti
                                        options={moduleOptions}
                                        value={moduleOptions.filter((opt) =>
                                            formData.selectedModules.includes(opt.value)
                                        )}
                                        onChange={handleModulesChange}
                                        placeholder="Select modules..."
                                    />
                                    
                                </div>



                            </div>
                        </div>

                    </div>
                </form>

            </div>
        </>

    );
};

// === Sub Components ===
const SectionHeader = ({ title }) => (
    <h4 className="border-bottom pb-2 mb-3 mt-4 text-dark fw-semibold">
        {title}
    </h4>
);

const InputField = ({
    name,
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    required,
    col = "6",
}) => (
    <div className={`col-md-${col} mb-3`}>
        <label htmlFor={name} className="form-label">
            {label} {required && <span className="text-danger">*</span>}
        </label>
        <input
            id={name}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            className="form-control"
            placeholder={placeholder}
            required={required}
            autoComplete="new-password"

        />
    </div>
);



export default UserForm;
